import { useState, useEffect, useCallback, useMemo } from "react";
import * as service from "./exchangeRateService";

/**
 * Hook React centralizado de tipos de cambio.
 * Wrapper delgado sobre exchangeRateService.
 *
 * API compatible con el antiguo ER/hooks/useExchangeRates para facilitar migración.
 *
 * @param {string} pais - "Chile" o "Perú"
 * @param {number} [selectedYear] - Año para cálculos mensuales (default: año actual)
 */
export const useExchangeRates = (pais, selectedYear = new Date().getFullYear()) => {
  const [loaded, setLoaded] = useState(service.isLoaded());

  useEffect(() => {
    if (service.isLoaded()) {
      setLoaded(true);
      return;
    }
    service.loadExchangeRates().then(() => setLoaded(true));
  }, []);

  // ─── Mapas crudos (compatibilidad con ER) ────────────────────

  const dolarMaps = useMemo(
    () =>
      loaded
        ? {
            actual: service.getDolarMap(pais),
            chile: service.getDolarMap("Chile"),
            peru: service.getDolarMap("Perú"),
          }
        : {},
    [loaded, pais]
  );

  const ufMaps = useMemo(() => (loaded ? { actual: service.getUFMap() } : {}), [loaded]);

  const dolarOtroPais = useMemo(() => {
    if (!loaded) return {};
    const otro = pais === "Chile" ? "Perú" : "Chile";
    return service.getDolarMap(otro);
  }, [loaded, pais]);

  // ─── Fallback dinámico ───────────────────────────────────────

  const dolarFallback = useMemo(
    () => ({
      Chile: loaded ? service.getDolarFallback("Chile") : 0,
      Perú: loaded ? service.getDolarFallback("Perú") : 0,
    }),
    [loaded]
  );

  // ─── Funciones de consulta ───────────────────────────────────

  const getDolarDia10 = useCallback(
    (monthIndex, dolarMap, fallback) => {
      // Compatibilidad: ER pasa (monthIndex, dolarMap, fallback)
      // pero el service solo necesita (monthIndex, pais, year)
      if (dolarMap && typeof dolarMap === "object") {
        // Llamada legacy: determinar país por el mapa pasado
        let paisLookup = pais;
        if (dolarMap === dolarMaps.chile) paisLookup = "Chile";
        else if (dolarMap === dolarMaps.peru) paisLookup = "Perú";
        return service.getDolarDia10(monthIndex, paisLookup, selectedYear);
      }
      return service.getDolarDia10(monthIndex, pais, selectedYear);
    },
    [pais, selectedYear, dolarMaps.chile, dolarMaps.peru]
  );

  const getUFValue = useCallback((fechaStr) => service.getUFValue(fechaStr), []);

  const getDolarValue = useCallback((fechaStr) => service.getDolarValue(fechaStr, pais), [pais]);

  const getDolarValueChile = useCallback((fechaStr) => service.getDolarValue(fechaStr, "Chile"), []);

  const getDolarValuePeru = useCallback((fechaStr) => service.getDolarValue(fechaStr, "Perú"), []);

  // Compatibilidad: ER llama calcularDolarMensual(dolarMap, fallback, yearOverride)
  const calcularDolarMensual = useCallback(
    (dolarMap, fallback, yearOverride) => {
      const year = yearOverride || selectedYear;
      // Determinar país a partir del mapa pasado
      let paisLookup = pais;
      if (dolarMap === dolarMaps.chile) paisLookup = "Chile";
      else if (dolarMap === dolarMaps.peru) paisLookup = "Perú";
      return service.calcularDolarMensual(paisLookup, year);
    },
    [pais, selectedYear, dolarMaps.chile, dolarMaps.peru]
  );

  // ─── Valores mensuales derivados ─────────────────────────────

  const dolarMensual = useMemo(
    () => (loaded ? service.calcularDolarMensual(pais, selectedYear) : Array(12).fill(0)),
    [loaded, pais, selectedYear]
  );

  const dolarMensualAnoAnterior = useMemo(
    () => (loaded ? service.calcularDolarMensual(pais, selectedYear - 1) : Array(12).fill(0)),
    [loaded, pais, selectedYear]
  );

  const dolarMensualChile = useMemo(
    () => (loaded ? service.calcularDolarMensual("Chile", selectedYear) : Array(12).fill(0)),
    [loaded, selectedYear]
  );

  const dolarMensualPeru = useMemo(
    () => (loaded ? service.calcularDolarMensual("Perú", selectedYear) : Array(12).fill(0)),
    [loaded, selectedYear]
  );

  const dolarMensualOtroPais = useMemo(() => {
    if (!loaded) return Array(12).fill(0);
    const otro = pais === "Chile" ? "Perú" : "Chile";
    return service.calcularDolarMensual(otro, selectedYear);
  }, [loaded, pais, selectedYear]);

  // ─── Cross-rate y tipo de cambio actual ──────────────────────

  const crossRate = useMemo(() => (loaded ? service.getCrossRate() : 0), [loaded]);

  const tipoCambioActual = useMemo(() => (loaded ? service.getDolarFallback(pais) : 0), [loaded, pais]);

  return {
    loaded,
    // Maps crudos (compatibilidad ER)
    dolarMaps,
    ufMaps,
    dolarOtroPais,
    // Fallback dinámico
    dolarFallback,
    // Funciones de consulta
    getDolarDia10,
    getUFValue,
    getDolarValue,
    getDolarValueChile,
    getDolarValuePeru,
    calcularDolarMensual,
    // Valores mensuales derivados
    dolarMensual,
    dolarMensualAnoAnterior,
    dolarMensualChile,
    dolarMensualPeru,
    dolarMensualOtroPais,
    // Extras (VP, Categorías)
    crossRate,
    tipoCambioActual,
  };
};
